import React, { useState } from 'react';
import { X, TestTube, CheckCircle, XCircle } from 'lucide-react';
import apiService from '../../services/api';
import { AccountMatchingRule, TestMatchingResponse } from '../../types/api';

interface TestModalProps {
  rule: AccountMatchingRule | null;
  isOpen: boolean;
  onClose: () => void;
}

export const TestModal: React.FC<TestModalProps> = ({
  rule,
  isOpen,
  onClose
}) => {
  const [testData, setTestData] = useState({
    account_string: '',
    security: '',
    trade_type: 'both' as 'buy' | 'sell' | 'both'
  });
  
  const [results, setResults] = useState<TestMatchingResponse[]>([]);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const testCases = [
    { account_string: 'INTERVAL', security: 'AAPL', trade_type: 'buy' as const, description: 'Standard INTERVAL account' },
    { account_string: 'INTERVAL BIDS', security: 'AAPL', trade_type: 'buy' as const, description: 'INTERVAL BIDS account' },
    { account_string: 'OASYS:12345', security: 'SPY', trade_type: 'both' as const, description: 'OASYS format account' },
    { account_string: 'maplelane fund', security: 'TSLA', trade_type: 'sell' as const, description: 'Maplelane account' },
  ];

  const runSingleTest = async (testInput: { account_string: string; security?: string; trade_type: 'buy' | 'sell' | 'both' }) => {
    setTesting(true);
    setError(null);
    
    try {
      const response = await apiService.testAccountMatching(testInput);
      const newResult = response.data!;
      setResults(prev => [newResult, ...prev.slice(0, 9)]); // Keep last 10 results
    } catch (err: any) {
      setError(err.response?.data?.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const runAllTestCases = async () => {
    setTesting(true);
    setError(null);
    setResults([]);
    
    try {
      const testPromises = testCases.map(test => 
        apiService.testAccountMatching(test).then(response => ({
          ...response.data!,
          description: test.description
        }))
      );
      
      const testResults = await Promise.allSettled(testPromises);
      const successfulResults = testResults
        .filter((result): result is PromiseFulfilledResult<TestMatchingResponse & { description: string }> => 
          result.status === 'fulfilled')
        .map(result => result.value);
      
      setResults(successfulResults);
    } catch (err: any) {
      setError('Some tests failed');
    } finally {
      setTesting(false);
    }
  };

  const handleCustomTest = () => {
    if (!testData.account_string.trim()) return;
    runSingleTest({
      account_string: testData.account_string,
      security: testData.security || undefined,
      trade_type: testData.trade_type
    });
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-4xl p-0 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Test Account Matching
              </h3>
              {rule && (
                <p className="text-sm text-gray-500 mt-1">
                  Testing against rule: {rule.pattern_value} ({rule.commission_rate}%)
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Test Input Section */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Custom Test</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="label">Account String</label>
                      <input
                        type="text"
                        value={testData.account_string}
                        onChange={(e) => setTestData(prev => ({ ...prev, account_string: e.target.value }))}
                        className="input"
                        placeholder="Enter account string to test..."
                      />
                    </div>

                    <div>
                      <label className="label">Security (Optional)</label>
                      <input
                        type="text"
                        value={testData.security}
                        onChange={(e) => setTestData(prev => ({ ...prev, security: e.target.value }))}
                        className="input"
                        placeholder="Enter security symbol..."
                      />
                    </div>

                    <div>
                      <label className="label">Trade Type</label>
                      <select
                        value={testData.trade_type}
                        onChange={(e) => setTestData(prev => ({ ...prev, trade_type: e.target.value as any }))}
                        className="input"
                      >
                        <option value="both">Both Buy & Sell</option>
                        <option value="buy">Buy Only</option>
                        <option value="sell">Sell Only</option>
                      </select>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={handleCustomTest}
                        disabled={!testData.account_string.trim() || testing}
                        className="btn btn-primary flex items-center space-x-2"
                      >
                        {testing && <div className="loading-spinner" />}
                        <TestTube className="w-4 h-4" />
                        <span>Test</span>
                      </button>
                      
                      <button
                        onClick={runAllTestCases}
                        disabled={testing}
                        className="btn btn-secondary flex items-center space-x-2"
                      >
                        {testing && <div className="loading-spinner" />}
                        <span>Run All Test Cases</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Predefined Test Cases */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Common Test Cases</h4>
                  
                  <div className="space-y-2">
                    {testCases.map((testCase, index) => (
                      <div key={index} className="p-3 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {testCase.account_string}
                            </div>
                            <div className="text-xs text-gray-500">
                              {testCase.description} • {testCase.security} • {testCase.trade_type}
                            </div>
                          </div>
                          <button
                            onClick={() => runSingleTest(testCase)}
                            disabled={testing}
                            className="btn btn-secondary btn-sm"
                          >
                            Test
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg">
                    <p className="text-sm text-danger-800">{error}</p>
                  </div>
                )}
              </div>

              {/* Results Section */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Test Results</h4>
                
                {results.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <TestTube className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p>No test results yet</p>
                    <p className="text-sm">Run a test to see results here</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {results.map((result, index) => (
                      <div key={index} className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {result.matched ? (
                              <CheckCircle className="w-5 h-5 text-success-600" />
                            ) : (
                              <XCircle className="w-5 h-5 text-danger-600" />
                            )}
                            <span className="font-medium text-gray-900">
                              {result.account_string}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatTimestamp(result.timestamp)}
                          </span>
                        </div>
                        
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Commission Rate:</span>
                            <span className={`font-medium ${result.matched ? 'text-success-600' : 'text-gray-900'}`}>
                              {result.commission_rate}%
                            </span>
                          </div>
                          
                          {result.security ? (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Security:</span>
                              <span className="text-gray-900">{result.security}</span>
                            </div>
                          ) : null}
                          
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Trade Type:</span>
                            <span className="text-gray-900">{result.trade_type}</span>
                          </div>
                          
                          {result.matched_rule ? (
                            <div className="mt-2 p-2 bg-success-50 rounded text-xs">
                              <div className="font-medium text-success-900">Matched Rule:</div>
                              <div className="text-success-800">
                                {result.matched_rule.description || result.matched_rule.pattern_value} 
                                (Priority: {result.matched_rule.priority})
                              </div>
                            </div>
                          ) : null}
                          
                          {!result.matched ? (
                            <div className="mt-2 p-2 bg-danger-50 rounded text-xs text-danger-800">
                              No matching rule found - using default commission rate
                            </div>
                          ) : null}
                          
                          {'description' in result && result.description ? (
                            <div className="text-xs text-gray-500 mt-1 italic">
                              {String(result.description)}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};